import { Grid } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Typography from '@material-ui/core/Typography'
import { Currency } from 'components/offer/OfferPrice'
import { DropType } from 'core/logic/drop/drop.types'
import { CurrenciesEnum } from 'core/types/CurrenciesEnum'
import React, { ComponentProps, useMemo, useState } from 'react'
import { i18n, t } from 'translate/i18n'
import { mapPaymentMethodToCurrency } from '../Payment.data'
import useStyles from '../Payment.style'
import {
  IPaymentStepMethodInputs,
  PaymentMethods,
  SelectedPaymentMethodInfo,
} from '../Payment.types'
import { methodLabelComponent } from './methods'
import { PaymentMethodTestIDs } from './StepMethod.e2e'
import Button from '@onepercentio/one-ui/components/Button'
import ActionsContainer from 'openspace/components/ActionsContainer'
import { MethodNProvider, usePaymentMethods } from 'context/Payment'
import { PaymentsProvider } from 'core/logic/tenant/tenant.types'
import useQuery from '@onepercentio/one-ui/hooks/utility/useQuery'

export interface PaymentViewProps {
  onNext: (data: IPaymentStepMethodInputs) => void
  drop?: DropType | null
  paymentMethods: MethodNProvider[]
  acceptedCurrencies: CurrenciesEnum[]
  selectedPaymentMethod?: SelectedPaymentMethodInfo
  onSelectedPaymentMethod: (selected: SelectedPaymentMethodInfo) => void
}

const Step3View: React.FC<PaymentViewProps> = ({
  onNext,
  drop: dropWithoutAmount,
  paymentMethods,
  acceptedCurrencies,
  selectedPaymentMethod,
  onSelectedPaymentMethod,
}) => {
  const amount = useQuery<{ amount: string }>().get('amount') ?? '1'
  const dropWithUnits = useMemo(() => {
    if (!dropWithoutAmount) return dropWithoutAmount
    const _amount = Number(amount)
    return {
      ...dropWithoutAmount,
      prices: Object.fromEntries(
        Object.entries(dropWithoutAmount.prices!).map(([p, v]) => [
          p,
          v * _amount,
        ])
      ),
      unitPrice: _amount * dropWithoutAmount?.unitPrice!,
    }
  }, [dropWithoutAmount, amount])
  const classes = useStyles()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [method, currency, provider] = event.target.value.split(',') as [
      PaymentMethods,
      CurrenciesEnum,
      PaymentsProvider
    ]

    onSelectedPaymentMethod({
      method,
      currency,
      provider,
    })
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    let method = selectedPaymentMethod!.method
    let currency = selectedPaymentMethod!.currency
    onNext({ metodo: method, ...(currency ? { currency } : {}) })
  }

  return (
    <form id='payment-form' onSubmit={handleSubmit}>
      <Box mt={6} mb={4}>
        <Typography variant='body1' component='p'>
          <strong>
            {i18n.t(
              'authenticated.payment.steps.stepMethod.selectFormOfPayment'
            )}
          </strong>
        </Typography>
      </Box>
      <Grid container direction='column'>
        <RadioGroup
          aria-label='payment method'
          name='payment-method'
          value={selectedPaymentMethod}
          onChange={handleChange}
          className={classes.paymentMethodGroup}
          data-testid={PaymentMethodTestIDs.PAYMENT_METHOD_SELECTOR_CONTAINER}>
          {paymentMethods.map(([paymentMethod, provider]) => {
            let currencyBasedOnMethod: CurrenciesEnum

            if (!dropWithUnits) return ''

            switch (paymentMethod) {
              case 'CREDIT_CARD':
                return acceptedCurrencies.map((acceptedCurrency) => {
                  return (
                    <PaymentMethod
                      key={acceptedCurrency}
                      selected={selectedPaymentMethod}
                      paymentMethod={paymentMethod}
                      currencyBasedOnMethod={acceptedCurrency}
                      drop={dropWithUnits}
                      acceptedCurrencies={acceptedCurrencies}
                      paymentProvider={provider}
                    />
                  )
                })
              default:
                currencyBasedOnMethod =
                  mapPaymentMethodToCurrency[paymentMethod]
                return (
                  <PaymentMethod
                    key={paymentMethod}
                    selected={selectedPaymentMethod}
                    paymentMethod={paymentMethod}
                    currencyBasedOnMethod={currencyBasedOnMethod}
                    drop={dropWithUnits}
                    acceptedCurrencies={acceptedCurrencies}
                    paymentProvider={provider}
                  />
                )
            }
          })}
        </RadioGroup>
      </Grid>
    </form>
  )
}

function PaymentMethod({
  drop,
  paymentMethod,
  currencyBasedOnMethod,
  selected,
  acceptedCurrencies,
  paymentProvider,
}: {
  drop: DropType
  paymentMethod: PaymentMethods
  paymentProvider: PaymentsProvider
  currencyBasedOnMethod: CurrenciesEnum
  selected?: SelectedPaymentMethodInfo
  acceptedCurrencies: CurrenciesEnum[]
}) {
  const classes = useStyles()
  const MethodLabel = methodLabelComponent[paymentMethod]

  // Sets the initial currency based on the payment method
  let currency: CurrenciesEnum | undefined = currencyBasedOnMethod

  // The amount of the drop based of on the currency
  let amount = drop?.prices?.[currency]

  // If there is no amount set for the currency
  if (!amount) {
    amount = drop?.unitPrice // Gets the amount from the unitPrice
    currency = drop?.currency // Gets the currency from the drop
  }

  // Shows the flag if the usd is accepted, but the currency is BRL
  const hasCreditCardUSDMethod =
    acceptedCurrencies.includes(CurrenciesEnum.USD) &&
    currency === CurrenciesEnum.BRL

  if (
    currencyBasedOnMethod === currency && // The currency (from method or the drop)
    amount &&
    currency
  ) {
    return (
      <FormControlLabel
        key={paymentMethod}
        value={`${paymentMethod},${currency},${paymentProvider}`}
        control={<Radio className={classes.paymentMethodRadio} />}
        data-testid={PaymentMethodTestIDs.PAYMENT_METHOD_CONTAINER}
        label={
          <MethodLabel
            flag={hasCreditCardUSDMethod}
            checked={
              selected?.method === paymentMethod &&
              selected?.currency === currency
            }
            price={<Currency amount={amount} currency={currency} />}
            currency={currency}
          />
        }
        classes={{ label: classes.paymentMethodItem }}
      />
    )
  } else {
    return null
  }
}

export default Step3View

export function IsolatedPaymentMethodSelector(
  props: Omit<
    ComponentProps<typeof Step3View>,
    'selectedPaymentMethod' | 'onSelectedPaymentMethod' | 'paymentMethods'
  > &
    ClickActions<'onBack'>
) {
  const [selected, onSelect] =
    useState<ComponentProps<typeof Step3View>['selectedPaymentMethod']>()
  const paymentMethods = usePaymentMethods()
  return (
    <>
      <Step3View
        selectedPaymentMethod={selected}
        onSelectedPaymentMethod={onSelect}
        paymentMethods={paymentMethods}
        {...props}
      />
      <ActionsContainer>
        <Button variant='outline' onClick={props.onBack}>
          {t('generic.goBack')}
        </Button>
        <Button
          variant='filled'
          disabled={!selected}
          onClick={() => {
            props.onNext({
              metodo: selected!.method,
              currency: selected!.currency,
              provider: selected!.provider,
            })
          }}
          color='primary'>
          {t('generic.continue')}
        </Button>
      </ActionsContainer>
    </>
  )
}
